export default () => ({
	quantity: 1,
	selectedOptions: {},
	variants: [],
	currentVariant: null,
	backInStockEmail: '',
	backInStockSuccess: false,
	backInStockError: '',

	init() {
		// Parse product JSON from script tag
		try {
			const productJsonElement = this.$refs.productJson
			if (!productJsonElement) {
				console.error('Product JSON element not found')
				return
			}

			const productJson = JSON.parse(productJsonElement.innerHTML || '{}')
			this.variants = productJson.variants || []

			// Check for variant in URL query parameters
			const urlParams = new URLSearchParams(window.location.search)
			const variantFromUrl = urlParams.get('variant')

			if (this.variants.length > 0) {
				let initialVariant

				if (variantFromUrl) {
					// Try to find variant from URL parameter
					initialVariant = this.variants.find(v => v.id === Number(variantFromUrl))
				}

				// Fall back to first available variant if URL variant not found
				if (!initialVariant) {
					// Find first available variant instead of using current_variant
					initialVariant = this.variants.find(v => v.available !== false) || this.variants[0]
				}

				this.currentVariant = initialVariant

				// Set initial options based on the selected variant
				if (initialVariant?.options) {
					this.selectedOptions = initialVariant.options.reduce((acc, opt) => {
						acc[opt.name] = opt.value
						return acc
					}, {})
				}
			}

			// Watch for changes to selected options
			this.$watch('selectedOptions', () => {
				this.updateVariant()
			})

			this.$watch('currentVariantId', () => {
				this.backInStockEmail = ''
				this.backInStockSuccess = false
				this.backInStockError = ''
			})
		} catch (error) {
			console.error('Error initializing product form:', error)
		}
	},

	increment() {
		this.quantity++
	},

	decrement() {
		if (this.quantity > 1) {
			this.quantity--
		}
	},

	updateOption(optionName, optionValue) {
		this.selectedOptions[optionName] = optionValue
	},

	updateVariant() {
		const selectedOptionsEntries = Object.entries(this.selectedOptions)

		if (selectedOptionsEntries.length === 0) return

		const matchingVariant = this.variants.find(variant => {
			if (!variant.options) return false

			return selectedOptionsEntries.every(([optionName, optionValue]) => {
				const option = variant.options.find(opt => opt.name === optionName)
				if (!option) return false

				return option.value === optionValue
			})
		})

		if (matchingVariant) {
			this.currentVariant = matchingVariant

			const idInput = this.$el.querySelector('[name="id"]')
			if (idInput) {
				idInput.value = matchingVariant.id
			}
		} else if (this.debug) {
			console.log('No matching variant found')
		}
	},

	isOptionAvailable(optionName, optionValue) {
		const testSelections = { ...this.selectedOptions }
		testSelections[optionName] = optionValue

		const matchingVariant = this.variants.find(variant => {
			if (!variant.options) return false

			const matches = Object.entries(testSelections).every(([name, value]) => {
				const option = variant.options.find(opt => opt.name === name)
				if (!option) return false

				const valueMatch = option.value === value
				return valueMatch
			})

			return matches
		})

		const isAvailable = matchingVariant && matchingVariant.available !== false

		return isAvailable
	},

	getAvailableOptionsFor(optionName) {
		const availableOptions = new Set()

		const currentSelections = { ...this.selectedOptions }
		delete currentSelections[optionName]

		this.variants.forEach(variant => {
			if (variant.available === false) return

			const matchesCurrentSelections = Object.entries(currentSelections).every(([name, value]) => {
				const option = variant.options.find(opt => opt.name === name)
				if (!option) return false

				return option.value === value
			})

			if (matchesCurrentSelections) {
				const option = variant.options.find(opt => opt.name === optionName)
				if (option) {
					availableOptions.add(option.value)
				}
			}
		})

		return Array.from(availableOptions)
	},

	async backInStock(email, variant) {
		const companyId = 'Uj8avs' // Klaviyo Public API Key
		const payload = {
			data: {
				type: 'back-in-stock-subscription',
				attributes: {
					profile: {
						data: {
							type: 'profile',
							attributes: {
								email: email,
							},
						},
					},
					channels: ['EMAIL'],
				},
				relationships: {
					variant: {
						data: {
							type: 'catalog-variant',
							id: `$shopify:::$default:::${variant}`,
						},
					},
				},
			},
		}

		const requestOptions = {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				revision: '2024-06-15',
			},
			body: JSON.stringify(payload),
		}

		const url = `https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=${companyId}`

		try {
			const response = await fetch(url, requestOptions)
			const text = await response.text()
			let data = null
			try {
				data = text ? JSON.parse(text) : null
			} catch (e) {
				// If the response is not valid JSON, keep data as null
			}

			if (response.status === 202) {
				// Success: request accepted, no body
				return
			}

			if (!response.ok) {
				throw new Error((data && data.message) || 'Klaviyo request failed')
			}
		} catch (err) {
			console.error('[backInStock] Error:', err)
			throw err
		}
	},

	async submitBackInStock() {
		if (!this.backInStockEmail || !this.currentVariantId) {
			this.backInStockError = 'Please enter your email.'
			return
		}
		try {
			await this.backInStock(this.backInStockEmail, this.currentVariantId)
			this.backInStockSuccess = true
			this.backInStockError = ''
		} catch (err) {
			this.backInStockError = 'There was a problem. Please try again.'
		}
	},

	get isAvailable() {
		return this.currentVariant?.available !== false
	},

	get currentPrice() {
		return this.currentVariant?.price || ''
	},

	get currentVariantId() {
		return this.currentVariant?.id || ''
	},

	get formattedPrice() {
		let price = this.currentPrice
		if (typeof price === 'string') {
			price = price.replace(/^\$/, '').trim()
		}
		return price
	},

	async submit() {
		if (!this.isAvailable) {
			return
		}

		const form = this.$el
		const formData = new FormData(form)
		const variant = formData.get('id')

		const payload = [
			{
				id: Number(variant),
				quantity: Number(this.quantity),
			},
		]

		const cart = this.$store.cart

		await cart.addLines(payload)
		this.$store.site.openCart()
	},
})
